import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldAlert, Lock } from "lucide-react";
import { ExecutarReset } from "@/components/ExecutarReset";
import { ResetExecutado } from "@/components/ResetExecutado";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dataBR = (s) => (s ? s.split("-").reverse().join("/") : "—");

function Card({ label, value, testid, hint }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">{label}</div>
      <div className="text-2xl text-stone-900 mt-2 font-semibold">{value}</div>
      {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
    </div>
  );
}

function StatusBox({ status, sim }) {
  return (
    <div className="bg-[#083A6B] text-white rounded-lg p-6 mb-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between" data-testid="reset-status-box">
      <div className="flex items-center gap-3">
        <Lock className="w-5 h-5 text-[#D4A652]" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/70">Status do reset</div>
          <div className="text-lg font-semibold" data-testid="reset-status">
            {status?.status === "nao_executado" ? "Não executado — somente simulação" : status?.status}
          </div>
        </div>
      </div>
      <div className="text-sm text-white/80">
        Abertura financeira: <b data-testid="reset-data-abertura">{dataBR(sim?.data_abertura)}</b> · Filtro nos indicadores:{" "}
        <b data-testid="reset-filtro-abertura">{status?.filtro_abertura_ativo ? "ligado" : "desligado até o reset"}</b> · Banco: <span data-testid="reset-banco">{sim?.banco}</span>
      </div>
    </div>
  );
}

function ContagensTabela({ sim }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto" data-testid="reset-tabela-colecoes">
      <Table>
        <TableHeader>
          <TableRow><TableHead>Coleção</TableHead><TableHead className="text-right">Seriam excluídos</TableHead><TableHead className="text-right">Permaneceriam</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(sim.contagens).map(([c, n]) => (
            <TableRow key={c} data-testid={`reset-colecao-${c}`}>
              <TableCell className="font-mono text-xs">{c}</TableCell>
              <TableCell className="text-right">{n}</TableCell>
              <TableCell className="text-right">{sim.registros_permanecem?.[c] ?? 0}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ContasEncerradas({ contas }) {
  if (!contas?.length) {
    return <p className="text-sm text-stone-600" data-testid="reset-contas-encerradas-vazio">Nenhuma conta antiga é citada pelos pagamentos preservados.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="reset-contas-encerradas">
      {contas.map((c) => (
        <li key={c.id} className="bg-white border border-stone-200 rounded-lg p-4 text-sm">
          <b>{c.nome || c.id}</b> · saldo hoje {brl(c.saldo_inicial_atual)} → após o reset: encerrada, saldo R$ 0,00, sem novos lançamentos
        </li>
      ))}
    </ul>
  );
}

export default function ResetFinanceiro() {
  const [sim, setSim] = useState(null);
  const [status, setStatus] = useState(null);
  const [erro, setErro] = useState("");
  const [executou, setExecutou] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([api.get("/admin/reset-financeiro/simulacao"), api.get("/admin/reset-financeiro/status")]);
        setSim(s.data);
        setStatus(st.data);
      } catch (e) {
        setErro(e?.response?.data?.detail || "Não foi possível carregar a simulação.");
      }
    })();
  }, []);

  const atualizarStatus = async () => {
    const st = await api.get("/admin/reset-financeiro/status");
    setStatus(st.data);
  };

  if (erro) return <div className="text-red-700 flex gap-2 items-center" data-testid="reset-erro"><ShieldAlert className="w-4 h-4" />{erro}</div>;
  if (!sim) return <div className="text-stone-500" data-testid="reset-carregando">Simulando…</div>;
  const fp = sim.futuras_preservadas;
  return (
    <div data-testid="reset-financeiro-page">
      <PageHeader eyebrow="Administração Mordomia Tech" title="Reset financeiro"
        description="Abrir esta tela não altera nada. A execução só acontece pelo botão, com a frase de confirmação." />
      <StatusBox status={status} sim={sim} />
      {status?.status === "executado" && !executou ? (
        <ResetExecutado status={status} onAtualizar={atualizarStatus} />
      ) : (
        <ExecutarReset sim={sim} status={status} onConcluido={() => setExecutou(true)} />
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card label="Reservas ajustadas (R1)" value={sim.reservas_r1} testid="reset-card-r1" hint="entrada antes da abertura" />
        <Card label="Reservas preservadas" value={sim.reservas_preservadas_sem_alteracao} testid="reset-card-preservadas" hint={`${sim.reservas_a_partir_abertura} com entrada a partir da abertura`} />
        <Card label="Registros a excluir" value={sim.total_registros} testid="reset-card-total" />
        <Card label="Valor pago preservado" value={brl(fp.valor_pago_total)} testid="reset-card-valor-preservado" hint={`${fp.com_valor_pago} reservas futuras`} />
        <Card label="Pagamentos mantidos" value={fp.registros_vinculados_preservados?.pagamentos ?? 0} testid="reset-card-pagamentos-mantidos" />
        <Card label="Cobranças PagBank mantidas" value={fp.registros_vinculados_preservados?.pagbank_checkouts ?? 0} testid="reset-card-pagbank-mantidos" />
        <Card label="Plano de contas" value={sim.plano_de_contas_preservado} testid="reset-card-plano" hint="preservado" />
        <Card label="Contas sem destino" value={sim.referencias_conta_inexistente?.length ?? 0} testid="reset-card-refs-sem-destino" />
      </div>
      <h2 className="text-base md:text-lg font-semibold text-stone-900 mb-3">Contas antigas citadas por pagamentos mantidos</h2>
      <div className="mb-8"><ContasEncerradas contas={sim.contas_antigas_encerradas} /></div>
      <h2 className="text-base md:text-lg font-semibold text-stone-900 mb-3">Arquivos de extrato que seriam apagados</h2>
      <ul className="mb-8 space-y-2" data-testid="reset-arquivos-apagar">
        {(sim.arquivos_a_apagar || []).map((a) => (
          <li key={a.caminho} className="bg-white border border-stone-200 rounded-lg p-4 text-sm break-all">{a.arquivo_nome || a.caminho}</li>
        ))}
        {!sim.arquivos_a_apagar?.length && <li className="text-sm text-stone-600">Nenhum arquivo.</li>}
      </ul>
      <h2 className="text-base md:text-lg font-semibold text-stone-900 mb-3">Registros por coleção</h2>
      <ContagensTabela sim={sim} />
    </div>
  );
}
